import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSearchParams } from "react-router-dom";

const APP_DEEP_LINK = "ekalsutraparentapp://app/fees";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.ekalsutraparentapp";

const SUCCESS_STATUSES = ["CHARGED", "SUCCESS", "COMPLETED"];
const PENDING_STATUSES = ["PENDING", "PENDING_VBV", "AUTHORIZING", "STARTED"];
const FAILED_STATUSES = [
  "AUTHENTICATION_FAILED",
  "AUTHORIZATION_FAILED",
  "FAILED",
  "JUSPAY_DECLINED",
  "AUTO_REFUNDED",
];

function resolveStatusMessage(status) {
  const normalized = (status || "").toUpperCase();

  if (SUCCESS_STATUSES.includes(normalized)) {
    return {
      tone: "success",
      title: "Payment Successful",
      message:
        "Your fee payment was completed successfully. You can safely return to the Ekalsutra Parent App.",
    };
  }

  if (PENDING_STATUSES.includes(normalized)) {
    return {
      tone: "pending",
      title: "Payment Pending",
      message:
        "Your payment is still being processed. Please check the payment status in the Parent App shortly.",
    };
  }

  if (FAILED_STATUSES.includes(normalized)) {
    return {
      tone: "failed",
      title: "Payment Failed",
      message:
        "We could not complete this payment. Please return to the Parent App and try again.",
    };
  }

  return {
    tone: "info",
    title: "Payment Response Received",
    message:
      "We have received the payment gateway response. Please open the Ekalsutra Parent App to view the final status.",
  };
}

function readPostedFormFields() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("order_id") || params.get("status")) {
      return {};
    }

    // Juspay may POST form fields to return_url; keep them if browser exposes them via history state.
    const state = window.history.state;
    if (state && typeof state === "object") {
      return state;
    }
  } catch (err) {
    console.log("Unable to read posted payment response fields", err);
  }
  return {};
}

const HandleJuspayResponse = () => {
  const [searchParams] = useSearchParams();
  const [postedFields] = useState(readPostedFormFields);
  const [redirecting, setRedirecting] = useState(false);

  const orderId =
    searchParams.get("order_id") ||
    searchParams.get("orderId") ||
    postedFields.order_id ||
    postedFields.orderId ||
    "";

  const status =
    searchParams.get("status") ||
    searchParams.get("order_status") ||
    postedFields.status ||
    postedFields.order_status ||
    "";

  const statusInfo = useMemo(() => resolveStatusMessage(status), [status]);

  useEffect(() => {
    // Soft redirect back into the Parent App after showing the response briefly.
    setRedirecting(true);
    const timer = setTimeout(() => {
      window.location.href = APP_DEEP_LINK;
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const iconSx = { fontSize: 48, color: "white" };
  const iconBg =
    statusInfo.tone === "success"
      ? "#2e7d32"
      : statusInfo.tone === "pending"
        ? "#ed6c02"
        : statusInfo.tone === "failed"
          ? "#d32f2f"
          : "#2789FD";

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: "50%",
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {statusInfo.tone === "success" ? (
            <CheckCircleOutlineIcon sx={iconSx} />
          ) : statusInfo.tone === "pending" ? (
            <HourglassEmptyIcon sx={iconSx} />
          ) : statusInfo.tone === "failed" ? (
            <ErrorOutlineIcon sx={iconSx} />
          ) : (
            <InfoOutlinedIcon sx={iconSx} />
          )}
        </Box>

        <Typography
          variant="h5"
          fontWeight={600}
          color="text.primary"
          gutterBottom
        >
          {statusInfo.title}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {statusInfo.message}
        </Typography>

        {orderId ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Order ID: {orderId}
          </Typography>
        ) : null}

        {status ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Status: {status}
          </Typography>
        ) : (
          <Box sx={{ mb: 3 }} />
        )}

        {redirecting ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Opening Ekalsutra Parent App...
            </Typography>
            <CircularProgress size={28} sx={{ mb: 3 }} />
          </>
        ) : null}

        <Button
          variant="contained"
          href={APP_DEEP_LINK}
          size="large"
          sx={{ textTransform: "none", px: 3, mb: 2 }}
        >
          Open Parent App
        </Button>

        <Button
          variant="text"
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          size="medium"
          sx={{ textTransform: "none" }}
        >
          Install from Play Store
        </Button>
      </Box>
    </Container>
  );
};

export default HandleJuspayResponse;
